import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { useHistory } from 'react-router-dom';
import SignatureCanvas from 'react-signature-canvas';
import Heading from '@tds/core-heading';
import DimpleDivider from '@tds/core-dimple-divider';
import Checkbox from '@tds/core-checkbox';
import Box from '@tds/core-box';
import Button from '@tds/core-button';
import TextButton from '@tds/core-text-button';
import Notification from '@tds/core-notification';
import Spinner from '@tds/core-spinner';
import queryString from 'query-string';

import request, { apiHost } from '../../utils/request';
import ValidateLoader from '../../UI/validateLoader';
import Loading from '../../signContract/loading';

import { ContentWrapper, SignatureContainer } from '../../design/css';
import { useLocalizedStringsContext } from '../../context/LocalizedStringsContext';

const SignIotContract = ({ location: { search } }) => {
  const localizedStrings = useLocalizedStringsContext();
  const {
    contractSignature: {
      headerIot,
      consetText,
      errorMessage,
      errorCheck,
      acceptButton,
      loadingMessage,
      submittingMessage,
      signatureText,
      clearText
    }
  } = localizedStrings;
  const history = useHistory();
  const [orderData, setOrderData] = useState();
  const [signature, setSignature] = useState(false);
  const [error, setError] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [isChecked, setIsChecked] = useState(false);
  const signCanvas = useRef();
  const { token, product } = queryString.parse(search);

  const getContractUrl = () => {
    const productNameForUrl = product.replaceAll(' ', '').toLowerCase();
    const { id } = orderData;
    return `${apiHost}/orders/${id}/iot-${productNameForUrl}-contract`;
  };

  useEffect(() => {
    const checkToken = async () => {
      const { data } = await request.get(
        `/orders/validate-token/?token=${token}`
      );

      if (data.success) {
        setOrderData(data);
      } else {
        history.push('/sign-iot-contract/expired');
      }
    };
    checkToken();
  }, []);

  const captureSignature = () => {
    if (signCanvas.current.isEmpty()) {
      setError(errorMessage);
      return;
    }
    if (!isChecked) {
      setError(errorCheck);
      return;
    }

    const signContract = async () => {
      setSubmitting(true);
      const payload = { token, signature };
      const res = await request.post('/orders/contract-signed', payload);
      setSubmitting(false);
      if (res.data.success) {
        history.push('/sign-iot-contract/success');
      } else {
        history.push('/sign-iot-contract/expired');
      }
    };

    signContract();
  };

  const clearSignature = () => {
    if (!signCanvas.current.isEmpty()) {
      signCanvas.current.clear();
      setSignature(null);
    }
  };

  const onChangeOfSignaturePad = () => {
    setError(false);
    setSignature(signCanvas.current.getTrimmedCanvas().toDataURL());
  };

  const onChange = () => setIsChecked(!isChecked);

  return !orderData ? (
    <Loading isVisible>
      <ValidateLoader message={loadingMessage} />
    </Loading>
  ) : (
    <>
      <div className="container 85%">
        <Spinner
          fullScreen={submitting}
          label={submittingMessage}
          spinning={submitting}
        />
        <ContentWrapper>
          <Heading level="h2">{headerIot}</Heading>
          <DimpleDivider />
          <embed
            src={getContractUrl()}
            type="application/pdf"
            width="100%"
            height="600px"
          />
          <DimpleDivider />
          <Box between={3}>
            <Box between={1}>
              <Checkbox
                name="consent"
                value="consent"
                label={consetText}
                onChange={onChange}
              />
            </Box>
            <Heading level="h4">{signatureText}</Heading>
            <SignatureContainer>
              <SignatureCanvas
                ref={signCanvas}
                onEnd={onChangeOfSignaturePad}
                penColor="green"
                canvasProps={{
                  width: 400,
                  height: 150,
                  className: 'sigCanvas'
                }}
              />
              {error && (
                <Notification variant="error" copy="en">
                  {error}
                </Notification>
              )}
            </SignatureContainer>
            {signature && (
              <div>
                <TextButton onClick={clearSignature}>{clearText}</TextButton>
              </div>
            )}
            <div>
              <Button onClick={captureSignature}>{acceptButton}</Button>
            </div>
          </Box>
        </ContentWrapper>
      </div>
    </>
  );
};

SignIotContract.propTypes = {
  location: PropTypes.objectOf(PropTypes.string).isRequired
};

export default SignIotContract;
